import { CanceledError as AxiosCanceledError, isAxiosError, type AxiosRequestConfig } from 'axios'
import { createIntl } from 'react-intl'

import * as Sentry from '@/src/config/sentry'
import { authTokenStorage } from '@/src/features/auth/utils/authTokenStorage'
import { Cookie } from '@/src/general/utils/cookies/Cookie'

import { Observable } from '../../classes/Observable'
import { CanceledException } from '../../errors/CanceledException'
import type { ICause } from '../../errors/Exception'
import { HTTPException } from '../../errors/HTTPException'
import { NetworkException } from '../../errors/NetworkException'
import type { TApiResponse } from '../fetcher/api.types'
import { axiosInstance } from './axiosInstance'
import { isCacheBlackListed } from './cacheBlackList'
import type { IFetcher, TMutateConfig, TQueryConfig, TRequestConfig } from './IFetcher'

import { allMessages } from '@/translations/translations.constants'

const isLegacyApiResponse = <Data>(data: unknown): data is TApiResponse<Data> =>
  typeof data === 'object' && data !== null && 'success' in data

const intl = createIntl({
  locale: 'en',
  messages: { ...allMessages.fr, ...allMessages.en },
})

export class AxiosFetcher implements IFetcher {
  public isValidToken = new Observable<boolean>(true)

  constructor() {
    const legacyToken = Cookie.get('jwtToken')
    this.setXAuthToken(authTokenStorage.getToken() || legacyToken || '')

    axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          isAxiosError(error) &&
          is401Response(error.status) &&
          isBadTokenErrorInCause({ errors: error.response?.data.errors })
        ) {
          this.isValidToken.set(false)
        }
        return Promise.reject(error)
      },
    )
  }
  private _token?: string

  private _getUnCachedUrl = () => axiosInstance.defaults.baseURL?.replace('-cache', '')

  private async fetchApi<Data, Payload = Partial<Data>>(
    url: string,
    data: Payload,
    {
      method,
      params,
      headers,
      signal,
      withCredentials,
    }: Pick<AxiosRequestConfig<Payload>, 'method' | 'signal'> & TRequestConfig,
  ): Promise<Data | undefined> {
    try {
      const isBlackListed = isCacheBlackListed(url)
      const fetchOptions: AxiosRequestConfig<Payload> = {
        headers: this.buildHeaders(headers),
        url,
        params,
        method,
        signal,
        ...(method?.toUpperCase() === 'GET' || method?.toUpperCase() === 'HEAD' ? {} : { data }),
        withCredentials,
      }
      if (isBlackListed) {
        fetchOptions.baseURL = this._getUnCachedUrl()
      }
      const { data: responseData, status } = await axiosInstance<TApiResponse<Data> | Data>(fetchOptions)

      // Firefox nav-cancel can yield status 0 with an empty body
      if (status === 0 || responseData === '') {
        throw new NetworkException()
      }

      if (isLegacyApiResponse<Data>(responseData)) {
        return this.handleLegacyApiData(responseData, status)
      }

      return responseData
    } catch (error) {
      this.handleApiError(error)
    }
  }

  async mutate<Data, Payload = Record<string, unknown>>(
    url: string,
    data: Payload,
    { method, params, headers, withCredentials }: TMutateConfig,
  ): Promise<Data> {
    // Void mutations (`mutate<void>`) resolve to undefined when the API returns `{ success: true }` only.
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- void vs payload Data
    return (await this.fetchApi(url, data, { method, params, headers, withCredentials })) as Data
  }

  async query<Data>(url: string, { headers, params, signal, withCredentials }: TQueryConfig): Promise<Data> {
    const data = await this.fetchApi<Data>(url, {}, { method: 'GET', params, headers, signal, withCredentials })
    // GETs must return a body; empty legacy success must not become Query cache data.
    if (data === undefined) throw new NetworkException()
    return data
  }

  setXAuthToken(token: string) {
    this._token = token
  }

  removeXAuthToken() {
    delete this._token
  }

  private handleLegacyApiData<Data>(data: TApiResponse<Data>, status: number): Data | undefined {
    if (typeof data !== 'object' || data === null) return undefined
    if (data.success) {
      return 'data' in data ? data.data : undefined
    }
    throw new HTTPException({
      status,
      cause: { errors: data.errors },
    })
  }

  private buildHeaders(headers: Record<string, string> = {}) {
    return { ...headers, 'X-Auth-Token': this._token }
  }

  private handleApiError(e: unknown): never {
    let error: Error | HTTPException
    let message: string | undefined = undefined
    let status: number | undefined = undefined
    let cause: ICause | undefined = undefined
    let key: string | undefined = undefined

    if (isAxiosError(e)) {
      if (e.response?.data?.errors?.length > 0) {
        const id = e.response?.data.errors[0].message
        message = intl.formatMessage({ id })
        key = id
      }
      status = e.status || e.response?.status

      cause = {
        key,
        status,
        errors: e.response?.data.errors || e.response?.data.violations || [],
        uri: e.config?.url || '',
      }
    }

    if (typeof e === 'string') error = new Error(e)
    else if (!(e instanceof Error)) error = new Error('An unexpected error occurred.', { cause: e })
    else if (!isAxiosError(e)) error = e
    else if (e.status) {
      error = new HTTPException({ status: e.status, message, cause })
      if (e.status >= 500) {
        Sentry.captureException.then((f) => f(error))
      }
    } else if (e instanceof AxiosCanceledError) error = new CanceledException()
    else if (e.message === 'Network Error') error = new NetworkException()
    else error = new Error('An unexpected error occurred.', { cause: e })

    throw error
  }
}

const isBadTokenErrorInCause = (cause: ICause | undefined) => {
  return cause?.errors?.some(
    (error) =>
      ('property' in error ? error.property === 'token' : error.propertyPath === 'token') &&
      error.message === 'invalid',
  )
}

const is401Response = (status?: number) => status === 401
